import { LinearGradient } from "@-/com.npmjs.expo-linear-gradient";
import { View } from "@-/com.npmjs.react-native";
import { useCSSVariable } from "@-/com.npmjs.uniwind";
import { useHasHydrated } from "@-/dev.improve.haniverse.lib.hydration";
import {
  SPACE_CHROME_COLOR_VARIABLE,
  SpaceBackdrop,
  type SpaceBackdropFade,
} from "@-/dev.improve.haniverse.components.ui.space-backdrop";
import { SCROLL_EDGE_FADE_HEIGHT } from "#scroll-edge-fade-layout";
import {
  SCROLL_EDGE_FADE_GRADIENT_AXIS,
  SCROLL_EDGE_FADE_RAMP,
  type ScrollEdgeFadeStop,
} from "#scroll-edge-fade-ramp";

export type ScrollEdgeFadeProps = {
  /**
   * Which edge of the parent the band hugs. `bottom` (the default) ramps downward, so content
   * thins out as it approaches the lower boundary; `top` mirrors it. `right` fades across a
   * vertical band at the right boundary; `left` mirrors it.
   */
  edge?: "bottom" | "top" | "right" | "left";
  /**
   * Thickness of the band without consuming layout space: its height on the `top`/`bottom` edges
   * and its width on the `left`/`right` edges.
   */
  height?: number;
  /**
   * Theme colour token the content dissolves INTO, as a `--color-*` variable name. Defaults to
   * `--color-surface`, the background a section body paints — pass the token of whatever
   * actually sits behind the scroller when it differs, or the band reads as a mismatched wash
   * instead of the content thinning out.
   */
  surfaceColorVariable?: string;
};

const fillStyle = {
  bottom: 0,
  left: 0,
  position: "absolute",
  right: 0,
  top: 0,
} as const;

const bottomContainerStyle = {
  bottom: 0,
  height: SCROLL_EDGE_FADE_HEIGHT,
  left: 0,
  position: "absolute",
  right: 0,
} as const;

const topContainerStyle = {
  height: SCROLL_EDGE_FADE_HEIGHT,
  left: 0,
  position: "absolute",
  right: 0,
  top: 0,
} as const;

/**
 * The same dissolve for a band over the experimental space background: the sky itself, masked by
 * the ramp, so content melts into the stars rather than into a flat colour that hides them.
 */
const SKY_FADES = {
  bottom: { ...SCROLL_EDGE_FADE_GRADIENT_AXIS.bottom, ramp: SCROLL_EDGE_FADE_RAMP },
  left: { ...SCROLL_EDGE_FADE_GRADIENT_AXIS.left, ramp: SCROLL_EDGE_FADE_RAMP },
  right: { ...SCROLL_EDGE_FADE_GRADIENT_AXIS.right, ramp: SCROLL_EDGE_FADE_RAMP },
  top: { ...SCROLL_EDGE_FADE_GRADIENT_AXIS.top, ramp: SCROLL_EDGE_FADE_RAMP },
} as const satisfies Record<NonNullable<ScrollEdgeFadeProps["edge"]>, SpaceBackdropFade>;

const WRAPPED_HSL_PATTERN = /^hsla?\(\s*([^)]+?)\s*\)$/u;
const HEX_PATTERN = /^#([\da-f]{6})$/iu;
const WRAPPED_RGB_PATTERN = /^rgba?\(\s*([^)]+?)\s*\)$/u;
const HSL_CHANNEL_SEPARATOR_PATTERN = /[\s,]+/u;

/**
 * Uniwind returns a `--color-*` token as raw HSL CHANNELS (`"0 0% 100%"`) on native, but an
 * already-wrapped colour (`hsl(...)` / `#fff` / `rgb(...)`) on web. Rebuild the colour with an
 * explicit alpha in whichever form it arrived, so each gradient stop varies ONLY in alpha — never
 * in luminance. A stop that shifts hue or lightness is what produces a grey/black veil.
 */
function surfaceColorWithAlpha(value: string, alpha: number): string {
  // Already-wrapped web form: `hsl(h s% l%)` / `hsl(h, s%, l%)`. Re-wrap with our alpha, dropping
  // any the token already carries (`h s% l% / a`).
  const wrappedHslChannels = WRAPPED_HSL_PATTERN.exec(value)?.[1];
  if (wrappedHslChannels !== undefined) {
    const [baseChannels = wrappedHslChannels] = wrappedHslChannels.split("/");
    const channels = baseChannels.trim().split(HSL_CHANNEL_SEPARATOR_PATTERN).slice(0, 3);
    return `hsla(${channels.join(", ")}, ${alpha})`;
  }

  const hexChannels = HEX_PATTERN.exec(value)?.[1];
  if (hexChannels !== undefined) {
    const red = Number.parseInt(hexChannels.slice(0, 2), 16);
    const green = Number.parseInt(hexChannels.slice(2, 4), 16);
    const blue = Number.parseInt(hexChannels.slice(4, 6), 16);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  }

  const wrappedRgbChannels = WRAPPED_RGB_PATTERN.exec(value)?.[1];
  if (wrappedRgbChannels !== undefined) {
    const [baseChannels = wrappedRgbChannels] = wrappedRgbChannels.split("/");
    const [red, green, blue] = baseChannels.split(",").map((part) => part.trim());
    if (red === undefined || green === undefined || blue === undefined) {
      throw new Error(`ScrollEdgeFade: malformed rgb() surface colour token "${value}"`);
    }
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  }

  // Native form: bare HSL channels. React Native requires `hsla` for alpha;
  // the CSS `hsl(... / alpha)` spelling is rejected by processColor.
  return `hsla(${value.trim().split(HSL_CHANNEL_SEPARATOR_PATTERN).join(", ")}, ${alpha})`;
}

/**
 * Progressive fade over the edge of a SCROLL CONTAINER, marking that its content continues past
 * the viewport. Content DISSOLVES into the surface colour across the band, so a row leaving the
 * viewport thins out instead of being sliced at a line.
 *
 * The band is OPACITY ONLY, on purpose. It previously also drew a masked `BlurView`, and that is
 * what made the edge read as a dark shade: `expo-blur`'s `light`/`dark` tints map to the legacy
 * `UIBlurEffect.Style.light` / `.dark`, which composite a grey vibrancy layer of their own on top
 * of the gaussian. Over white content that layer has nowhere to go but darker, so the band greyed
 * the rows it was supposed to make disappear. A blur may only ever come back here if it can be
 * shown not to shade — a plain gaussian with no tint material.
 *
 * Every stop of the ramp is the SAME colour and differs only in alpha, for the same reason: a
 * gradient whose stops vary in luminance paints a wash, not a fade.
 *
 * Because the band belongs to the scrolling content — not to whatever chrome happens to sit
 * below it — a pinned toolbar, a pill bar or a sticky section header rendered outside the
 * scroller stays perfectly sharp while the content sliding under it fades.
 *
 * The dissolve is deliberately NOT real transparency: the band is an overlay, so it cannot
 * subtract alpha from the content beneath. It instead ramps the SURFACE COLOUR in over that
 * content, which is indistinguishable from the content fading out as long as
 * `surfaceColorVariable` matches what is actually behind the scroller.
 *
 * Render it as the LAST child of a `position: relative` parent that also holds the scroller, so
 * it overlays the scroller's final pixels without consuming layout height. It is
 * `pointerEvents="none"`, so it never intercepts taps or scrolls on the content beneath.
 */
export function ScrollEdgeFade({
  edge = "bottom",
  height = SCROLL_EDGE_FADE_HEIGHT,
  surfaceColorVariable = "--color-surface",
}: ScrollEdgeFadeProps) {
  const surfaceChannels = useCSSVariable(surfaceColorVariable);
  const hasHydrated = useHasHydrated();
  const isSky = surfaceColorVariable === SPACE_CHROME_COLOR_VARIABLE;
  const isHorizontal = edge === "left" || edge === "right";
  const containerClassName =
    edge === "left"
      ? "absolute inset-y-0 left-0"
      : edge === "right"
        ? "absolute inset-y-0 right-0"
        : undefined;
  const containerStyle = isHorizontal
    ? { width: height }
    : { ...(edge === "bottom" ? bottomContainerStyle : topContainerStyle), height };

  if (isSky) {
    return (
      <View
        className={containerClassName}
        pointerEvents="none"
        style={containerStyle}
        testID={`scroll-edge-fade-${edge}`}
      >
        <SpaceBackdrop fade={SKY_FADES[edge]} />
      </View>
    );
  }

  // `useCSSVariable` has no value while server rendering on web (there is no document to read
  // the theme from), so that pass draws no band rather than washing the edge with a wrong (or
  // black) colour. The hydrating client render must match it, so the band appears only after
  // hydration. A typo'd token also lands here (uniwind logs a dev warning for it).
  if (surfaceChannels === undefined || !hasHydrated) {
    // oxlint-disable-next-line haniverse/no-return-null -- web SSR: `useCSSVariable` has no document to resolve the theme token from
    return null;
  }
  const surfaceValue = String(surfaceChannels);

  const [firstStop, secondStop, ...remainingStops] = SCROLL_EDGE_FADE_RAMP;
  const toColor = ([, alpha]: ScrollEdgeFadeStop) => surfaceColorWithAlpha(surfaceValue, alpha);
  const toLocation = ([fraction]: ScrollEdgeFadeStop) => fraction;

  return (
    <View
      className={containerClassName}
      pointerEvents="none"
      style={containerStyle}
      testID={`scroll-edge-fade-${edge}`}
    >
      <LinearGradient
        colors={[
          toColor(firstStop),
          toColor(secondStop),
          ...remainingStops.map((stop) => toColor(stop)),
        ]}
        end={SCROLL_EDGE_FADE_GRADIENT_AXIS[edge].end}
        locations={[
          toLocation(firstStop),
          toLocation(secondStop),
          ...remainingStops.map((stop) => toLocation(stop)),
        ]}
        start={SCROLL_EDGE_FADE_GRADIENT_AXIS[edge].start}
        style={fillStyle}
      />
    </View>
  );
}
